import { useFavoriteToggle } from '../hooks/useFavoriteToggle';
import { I } from '../lib/icons';

type Props = {
  assetId: string;
  /** Server-State (`AssetListItem.favorited`); `undefined` → nicht favorisiert. */
  favorited: boolean | undefined;
  /**
   * `'card'` = Grid-Overlay (absolut `top-2 right-2` AM Thumbnail — Redesign S2:
   * Checkbox oben links, Schnellansicht/Download links neben dem Stern; sichtbar
   * bei Hover/Focus ODER wenn favorisiert, favorisiert = grün gefüllt, nicht
   * mehr gelb — Gelb gehört dem Hochladen-CTA). MUSS in eine
   * `pointer-events-none`-Thumbnail-Ebene gewrappt werden (der Button re-
   * aktiviert pointer-events selbst). `'row'` = Tabellen-Zelle (inline, sichtbar).
   */
  variant: 'card' | 'row';
};

/**
 * ⭐-Toggle für Library-Grid (AssetCard) + -Liste (AssetTable). Eigener
 * Component (NICHT der Hook direkt in AssetCard), damit `useToast()` nur läuft,
 * wenn der Stern tatsächlich gerendert wird (= nur Library) — sonst müsste jeder
 * AssetCard-Nutzer/Test einen ToastProvider stellen. Klick toggelt nur den
 * Favoriten und navigiert NICHT (stopPropagation + preventDefault).
 */
export function FavoriteStarButton({ assetId, favorited: initial, variant }: Props) {
  const { favorited, toggle, pending } = useFavoriteToggle(assetId, initial);
  const isCard = variant === 'card';
  const label = favorited ? 'Aus Favoriten entfernen' : 'Zu Favoriten hinzufügen';

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle();
      }}
      disabled={pending}
      aria-pressed={favorited}
      aria-label={label}
      title={label}
      className={
        isCard
          ? `pointer-events-auto absolute top-2 right-2 z-10 w-7 h-7 rounded-md flex items-center justify-center bg-white/90 shadow-sm transition-opacity cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 focus-visible:ring-offset-1 disabled:cursor-not-allowed ${
              favorited
                ? 'opacity-100 text-brand-green-700'
                : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-brand-green-700 hover:text-brand-green-900'
            }`
          : `shrink-0 w-7 h-7 flex items-center justify-center rounded-md transition cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 disabled:cursor-not-allowed ${
              favorited ? 'text-brand-green-700' : 'text-brand-green-300 hover:text-brand-green-700'
            }`
      }
    >
      <I.star size={isCard ? 14 : 16} weight={favorited ? 'fill' : 'regular'} />
    </button>
  );
}
